import React, { useEffect, useState, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import * as XLSX from "xlsx";
import Dashboard from "./dashboard";

// ✅ Safe environment fallback + sanity check
const AUDIENCE =
  import.meta.env?.VITE_AUTH0_AUDIENCE || "https://tilsubscribe_api/";
const API_BASE_URL =
  import.meta.env?.VITE_API_BASE_URL || "https://api.tilsubscribe.com";

// 🔍 Log environment sanity check once on load
console.log("🔧 Env Sanity Check:");
console.log("VITE_AUTH0_DOMAIN:", import.meta.env?.VITE_AUTH0_DOMAIN);
console.log("VITE_AUTH0_CLIENT_ID:", import.meta.env?.VITE_AUTH0_CLIENT_ID);
console.log("VITE_AUTH0_AUDIENCE:", import.meta.env?.VITE_AUTH0_AUDIENCE);
console.log("VITE_API_BASE_URL:", import.meta.env?.VITE_API_BASE_URL);
console.log("Resolved Audience:", AUDIENCE);
console.log("Resolved API Base URL:", API_BASE_URL);

function flattenForRow(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    out[k] = typeof v === "object" && v !== null ? JSON.stringify(v) : v;
  }
  return out;
}

export default function SubmissionHistory() {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    loginWithRedirect,
    user,
  } = useAuth0();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  // ✅ Check for admin role directly from access token
  const checkAdminRole = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: AUDIENCE },
      });

      const decoded = JSON.parse(atob(token.split(".")[1]));
      console.log("🧾 Token audience:", decoded.aud);
      console.log("🧾 Token roles:", decoded["https://til.app/claims/roles"]);

      const roles = decoded["https://til.app/claims/roles"] || [];
      setIsAdmin(Array.isArray(roles) && roles.includes("admin"));
    } catch (e) {
      console.warn("⚠️ Failed to check admin role:", e);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (isAuthenticated) checkAdminRole();
  }, [isAuthenticated, checkAdminRole]);

  // 📡 Fetch submissions (admin → all, normal user → own)
  const fetchData = useCallback(async () => {
    try {
      if (!isAuthenticated) {
        await loginWithRedirect();
        return;
      }

      setLoading(true);
      setFetchError("");

      const token = await getAccessTokenSilently({
        authorizationParams: { audience: AUDIENCE },
      });

      const endpoint = isAdmin
        ? `${API_BASE_URL}/api/forms/all`
        : `${API_BASE_URL}/api/forms`;

      console.log("📡 Fetching submissions from:", endpoint);

      const res = await fetch(endpoint, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) throw new Error("Unauthorized — invalid token");
      if (res.status === 403)
        throw new Error("Forbidden — admin access required");
      if (!res.ok) throw new Error(`Failed to fetch submissions (${res.status})`);

      const data = await res.json();
      setSubmissions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ fetchData error:", err);
      setFetchError(err.message || "Error fetching submissions");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, loginWithRedirect, getAccessTokenSilently, isAdmin]);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [fetchData, isAuthenticated]);

  // 🧮 Excel Export
  const downloadAllAsExcel = () => {
    if (!submissions.length) return;

    const rows = submissions.map((s) => ({
      id: s.id,
      createdAt: new Date(s.createdAt).toISOString(),
      ...flattenForRow(s.data),
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Submissions");
    XLSX.writeFile(wb, "submissions.xlsx");
  };

  const downloadOneAsExcel = (sub) => {
    const row = {
      id: sub.id,
      createdAt: new Date(sub.createdAt).toISOString(),
      ...flattenForRow(sub.data),
    };
    const ws = XLSX.utils.json_to_sheet([row]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Response");
    XLSX.writeFile(wb, `formresponse_${sub.id}.xlsx`);
  };

  if (loading) return <div>Loading submissions…</div>;
  if (fetchError) return <div style={{ color: "red" }}>{fetchError}</div>;
  if (submissions.length === 0) return <div>No submissions yet.</div>;

  return (
    <div>
      <Dashboard
        submissions={submissions}
        loading={loading}
        fetchError={fetchError}
        fetchData={fetchData}
      />

      <h2>
        {isAdmin ? "All Submission History (Admin)" : "Your Submission History"}
      </h2>

      <div style={{ marginBottom: "1rem" }}>
        <button onClick={downloadAllAsExcel}>Download all as .xlsx</button>
        <button onClick={fetchData} style={{ marginLeft: 8 }}>
          Refresh
        </button>
      </div>

      <ul style={{ listStyle: "none", padding: 0 }}>
        {submissions.map((sub) => (
          <li
            key={sub.id}
            style={{
              marginBottom: "1rem",
              paddingBottom: "1rem",
              borderBottom: "1px solid #eee",
            }}
          >
            <div>
              <b>ID:</b> {sub.id}
            </div>
            <div>
              <b>Submitted:</b> {new Date(sub.createdAt).toLocaleString()}
            </div>
            {isAdmin && sub?.data?.email && (
              <div>
                <b>User:</b> {sub.data.email}
              </div>
            )}

            <div style={{ marginTop: 8 }}>
              <button onClick={() => downloadOneAsExcel(sub)}>
                Download this as .xlsx
              </button>
            </div>

            <details style={{ marginTop: 8 }}>
              <summary>
                <b>View raw JSON</b>
              </summary>
              <pre style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>
                {JSON.stringify(sub.data, null, 2)}
              </pre>
            </details>
          </li>
        ))}
      </ul>
    </div>
  );
}
